import React, { useState } from 'react';
import styled from 'styled-components';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { TelemetryDataPoint } from '../../types/api';

/**
 * Props for the TelemetryChart component.
 */
interface TelemetryChartProps {
  data: TelemetryDataPoint[];
  height?: number;
}

/**
 * Available metrics that can be displayed in the chart.
 */
type MetricType = 'batteryLevel' | 'voltage' | 'temperature' | 'humidity' | 'pressure' | 'rssi' | 'snr';

/**
 * Configuration for each metric.
 */
interface MetricConfig {
  label: string;
  color: string;
  unit: string;
  visible: boolean;
}

/**
 * TelemetryChart component.
 * Displays telemetry data in a line chart.
 */
const TelemetryChart: React.FC<TelemetryChartProps> = ({ data, height = 300 }) => {
  // Initial metric configurations
  const [metrics, setMetrics] = useState<Record<MetricType, MetricConfig>>({
    batteryLevel: { label: 'Battery', color: '#27ae60', unit: '%', visible: true },
    voltage: { label: 'Voltage', color: '#3498db', unit: 'V', visible: true },
    temperature: { label: 'Temperature', color: '#e74c3c', unit: '°C', visible: false },
    humidity: { label: 'Humidity', color: '#9b59b6', unit: '%', visible: false },
    pressure: { label: 'Pressure', color: '#f39c12', unit: 'hPa', visible: false },
    rssi: { label: 'RSSI', color: '#1abc9c', unit: 'dBm', visible: false },
    snr: { label: 'SNR', color: '#34495e', unit: 'dB', visible: false }
  });

  // Format the timestamp for display
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Toggle metric visibility
  const toggleMetric = (metric: MetricType) => {
    setMetrics(prev => ({
      ...prev,
      [metric]: {
        ...prev[metric],
        visible: !prev[metric].visible
      }
    }));
  };

  // Check if data is available
  if (!data || data.length === 0) {
    return (
      <EmptyChart height={height}>
        <p>No telemetry data available</p>
      </EmptyChart>
    );
  }

  // Prepare data for the chart (reverse to show oldest to newest)
  const chartData = [...data].reverse().map(point => ({
    ...point,
    time: formatTimestamp(point.timestamp)
  }));

  return (
    <ChartContainer>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" />
          <YAxis />
          <Tooltip 
            formatter={(value, name) => {
              const metricKey = Object.keys(metrics).find(
                key => metrics[key as MetricType].label === name
              ) as MetricType;
              return [`${value} ${metrics[metricKey].unit}`, name];
            }}
          />
          <Legend onClick={(e) => {
            const metricKey = Object.keys(metrics).find(
              key => metrics[key as MetricType].label === e.dataKey
            ) as MetricType;
            toggleMetric(metricKey);
          }} />
          
          {Object.entries(metrics).map(([key, config]) => {
            if (!config.visible) return null;
            
            // Only show the line if the data has this metric
            if (data.every(point => point[key as keyof TelemetryDataPoint] === undefined)) {
              return null;
            }
            
            return (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                name={config.label}
                stroke={config.color}
                activeDot={{ r: 8 }}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
      
      <MetricsSelector>
        {Object.entries(metrics).map(([key, config]) => {
          // Only show the toggle if the data has this metric
          if (data.every(point => point[key as keyof TelemetryDataPoint] === undefined)) {
            return null;
          }
          
          return (
            <MetricToggle 
              key={key}
              active={config.visible}
              color={config.color}
              onClick={() => toggleMetric(key as MetricType)}
            >
              {config.label}
            </MetricToggle>
          );
        })}
      </MetricsSelector>
    </ChartContainer>
  );
};

/**
 * Styled chart container.
 */
const ChartContainer = styled.div`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.space.md};
`;

/**
 * Styled empty chart placeholder.
 */
const EmptyChart = styled.div<{ height: number }>`
  width: 100%;
  height: ${props => props.height}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.radii.md};
  color: ${({ theme }) => theme.colors.textLight};
`;

/**
 * Styled metrics selector container.
 */
const MetricsSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.xs};
  margin-top: ${({ theme }) => theme.space.sm};
`;

/**
 * Styled metric toggle button.
 */
const MetricToggle = styled.button<{ active: boolean; color: string }>`
  padding: ${({ theme }) => `${theme.space.xs} ${theme.space.sm}`};
  border-radius: ${({ theme }) => theme.radii.sm};
  border: 1px solid ${props => props.color};
  background-color: ${props => props.active ? props.color : 'transparent'};
  color: ${props => props.active ? 'white' : props.color};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.active ? props.color : `${props.color}22`};
  }
`;

export default TelemetryChart; 